import { useRef, useState } from "react";
import axios from "axios";
import { API, getAuthenticatedMediaUrl } from "@/App";
import { Camera, ImagePlus, Loader2, Clock, Check } from "lucide-react";

/**
 * Pieces shared by FSD fuel receipts and the members' tickets: the photo
 * picker (camera or gallery, shrunk before upload), a date/time field in New
 * York time with a "Now" button, and the payment-method chips.
 */

// ---------- New York date/time ----------

const NY = "America/New_York";

// "YYYY-MM-DDTHH:mm" in New York for a Date (what <input type="datetime-local"> takes).
export function toNYInput(date = new Date()) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: NY, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(date).map((x) => [x.type, x.value])
  );
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

// The reverse: a New York "YYYY-MM-DDTHH:mm" as a real instant (ISO, UTC).
export function fromNYInput(value) {
  if (!value) return null;
  const [d, t = "00:00"] = value.split("T");
  const [y, m, day] = d.split("-").map(Number);
  const [h, min] = t.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, day, h, min);
  // New York's offset at that moment (handles EST/EDT).
  const asNY = new Date(new Date(guess).toLocaleString("en-US", { timeZone: NY }));
  const asUTC = new Date(new Date(guess).toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess + (asUTC - asNY)).toISOString();
}

export function nyText(iso, withTime = true) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: NY, month: "short", day: "numeric",
      ...(withTime ? { hour: "numeric", minute: "2-digit" } : { year: "numeric" }),
    });
  } catch { return ""; }
}

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Who submitted a receipt: their unit is enough; their name only without one.
export const submitter = (r) => r?.submitted_unit || r?.submitted_by_name || "";

const inputCls = "w-full h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-slate-900 dark:text-slate-100";

/** Date + time in New York, filled in with now; "Now" puts it back to now. */
export function WhenField({ value, onChange, label = "Date & time" }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">{label}</div>
      <div className="flex gap-2">
        <input
          type="datetime-local"
          value={value}
          max={toNYInput(new Date(Date.now() + 60_000))}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => onChange(toNYInput())}
          className="shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-700 px-3 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <Clock className="w-4 h-4" />Now
        </button>
      </div>
    </div>
  );
}

// ---------- photo ----------

// Phone photos are 3-8 MB; ~1600 px JPEG keeps a receipt readable at a
// fraction of that. Anything the browser can't draw (e.g. HEIC) goes up as is.
async function shrink(file) {
  if (!file.type.startsWith("image/") || file.size < 400_000) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", await shrink(file));
  const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
  return res.data.url; // "/api/uploads/<id>"
}

export const thumbUrl = (url, size = 400) => (url ? getAuthenticatedMediaUrl(`${url}/thumb?size=${size}`) : "");

/** Take a photo or pick one; uploads straight away and hands back its url. */
export function PhotoField({ value, onChange, label = "Photo", onOpen }) {
  const cam = useRef(null);
  const pick = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const take = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    setErr("");
    try {
      onChange(await uploadPhoto(f));
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't upload the photo — try again");
    } finally {
      setBusy(false);
    }
  };

  const btn = "flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50";
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">{label}</div>
      {value && (
        <button type="button" onClick={() => onOpen?.(value)} className="block mb-2">
          <img src={thumbUrl(value, 600)} alt="" className="max-h-48 rounded-md border border-slate-200 dark:border-slate-700 object-contain bg-slate-50 dark:bg-slate-950" />
        </button>
      )}
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={() => cam.current?.click()} className={btn}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {value ? "Retake" : "Take photo"}
        </button>
        <button type="button" disabled={busy} onClick={() => pick.current?.click()} className={btn}>
          <ImagePlus className="w-4 h-4" />{value ? "Replace" : "Choose photo"}
        </button>
      </div>
      {err && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{err}</p>}
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={take} />
      <input ref={pick} type="file" accept="image/*" className="hidden" onChange={take} />
    </div>
  );
}

// ---------- payment methods ----------

export const PAY_METHODS = [
  { key: "cash", label: "Cash" },
  { key: "zelle", label: "Zelle" },
  { key: "cashapp", label: "Cash App" },
  { key: "paypal", label: "PayPal" },
  { key: "check", label: "Check" },
  { key: "company_card", label: "Company card" },
];
export const methodLabels = (keys) =>
  (keys || []).map((k) => PAY_METHODS.find((m) => m.key === k)?.label || k).join(" + ");

/** Any number of methods — part cash, part Zelle is fine. */
export function MethodChips({ value = [], onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2">
      {PAY_METHODS.map((m) => {
        const on = value.includes(m.key);
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => toggle(m.key)}
            aria-pressed={on}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              on
                ? "bg-emerald-600 border-emerald-600 text-white"
                : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            {on && <Check className="w-3.5 h-3.5" />}{m.label}
          </button>
        );
      })}
    </div>
  );
}

export const fieldInput = inputCls;
